import {
	type ChangeEvent,
	type FC,
	type FocusEvent,
	type KeyboardEvent,
	type MouseEvent,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { type UrlObject } from 'url';

import {
	type SelectChangeEventType,
	type SelectValue,
	type SelectOptionDefinition,
} from '@mui/base/useSelect/useSelect.types';
import { AccoStars } from '@vakantiesnl/components/src/atoms/AccoStars';
import { Icon as IconComponent } from '@vakantiesnl/components/src/atoms/Icon';
import { InputAdornmentComponent } from '@vakantiesnl/components/src/atoms/InputAdornment';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { Accordion } from '@vakantiesnl/components/src/molecules/Accordion';
import { type CheckBox, CheckboxGroup } from '@vakantiesnl/components/src/molecules/CheckboxGroup';
import { FiltersLabels } from '@vakantiesnl/components/src/molecules/FilterLabelList';
import { defaultParty } from '@vakantiesnl/components/src/molecules/MultiRoomPartyPicker';
import { PriceRangeFilter } from '@vakantiesnl/components/src/molecules/PriceRangeFilter';
import { RadioGroup } from '@vakantiesnl/components/src/molecules/RadioGroup';
import { SelectFormField, SelectFormFieldMultiRoom } from '@vakantiesnl/components/src/molecules/SelectFormField';
import { SelectFormFieldCalendar } from '@vakantiesnl/components/src/molecules/SelectFormField/Calendar/SelectFormFieldCalendar';
import { isZoover } from '@vakantiesnl/components/src/utils';
import { formatHiddenSearchFilterLink } from '@vakantiesnl/components/src/utils/search/formatHiddenSearchFilterLink';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { getAllowTransportFilters } from '@vakantiesnl/services/src/constants/setOwnTransport';
import { type Entity } from '@vakantiesnl/services/src/gtm/search';
import { useParseDurationsFilterConfigLister } from '@vakantiesnl/services/src/hooks/filter-hooks';
import { type GeoState, useGetZooverCompositeSearch } from '@vakantiesnl/services/src/hooks/queries';
import { useApplicationStore } from '@vakantiesnl/services/src/stores';
import {
	type FilterState,
	getSelectedChainsBySlugs,
	getSelectedTourOperatorsBySlugs,
	type RemoveFilterKey,
	type TransportType,
	getSelectedDurations,
	useFilterStore,
	STAR_OPTIONS,
} from '@vakantiesnl/services/src/stores/filtersStore';
import { useGlobalFiltersStore } from '@vakantiesnl/services/src/stores/globalFiltersStore';
import type { GlobalFiltersState } from '@vakantiesnl/services/src/stores/globalFiltersStore/interfaces';
import { parseToDurationNumber, sortNumberArray } from '@vakantiesnl/services/src/util/durations';
import { getSelectedCountriesFromSlugs, getSelectedGeoFromSlugs } from '@vakantiesnl/services/src/util/geo';
import { buildSummarizedPartyString } from '@vakantiesnl/services/src/util/partyUtils';
import { inject } from '@vakantiesnl/services/src/util/template';
import { type Elastic, type MicroCopy, type Search } from '@vakantiesnl/types';
import { isEqual, isString } from 'lodash';
import { useRouter } from 'next/router';

import routeParams from '@RouteParams';

import useStyles from './FilterList.style';
import { type OptionProp } from './FilterList.types';
import { FilterListSkeleton } from './FilterListSkeleton';

type Props = {
	microCopies: MicroCopy;
	partyComposition: Search.PartyComposition[];
	setPartyComposition: (party: Search.PartyComposition[]) => void;
	setDepartureDate: (date: string[]) => void;
	departureDate: FilterState['departureDate'];
	toggleDepartureOffset: (checked: boolean | number) => void;
	setFlexibleDepartureDate: (flexibleDays: boolean | number) => void;
	flexibleDepartureDate: FilterState['flexibleDepartureDate'];
	geo: GeoState;
	entitySearch: Entity | null;
	showStars?: boolean;
	onFilterRemove: (event: MouseEvent, filterType: RemoveFilterKey, value: string) => void;
	durationsRange?: OptionProp;
	durationsRangeChange: (option: OptionProp, setFilter: boolean) => void;
	selectedRange?: OptionProp;
	onClear: () => void;
	isLoading: boolean;
};

export type FilterOptionsKeys =
	| 'durations'
	| 'accoType'
	| 'pool'
	| 'distances'
	| 'awards'
	| 'stars'
	| 'facilities'
	| 'rating'
	| 'mealplans'
	| 'departureAirport'
	| 'transport'
	| 'themes'
	| 'countrySlugs'
	| 'regionSlugs'
	| 'citySlugs'
	| 'chainsSlugs'
	| 'tourOperatorsSlugs';

enum PriceFilterValidationError {
	MinimumExceedsMaximum = 'min_exceeds_max',
}

const priceOrUndefined = (price: string | null): number | undefined => {
	return price ? parseInt(price, 10) : undefined;
};

const durationStartAdornment = {
	startAdornment: (
		<InputAdornmentComponent position="start" type="icon">
			<IconComponent type="time" />
		</InputAdornmentComponent>
	),
};

export const FilterList: FC<Props> = ({
	departureDate,
	microCopies,
	partyComposition,
	setDepartureDate,
	setPartyComposition,
	setFlexibleDepartureDate,
	flexibleDepartureDate,
	geo,
	entitySearch,
	showStars = true,
	onFilterRemove,
	durationsRange,
	durationsRangeChange,
	selectedRange,
	onClear,
	isLoading,
}) => {
	const { classes: styles } = useStyles();
	const { query } = useRouter();
	const { isMobile, isTablet, isDesktop } = useBreakpoints();

	const isBot = useApplicationStore((s) => s.isBot);
	const stateFilters = useFilterStore((s) => s.filters);
	const globalFilters = useGlobalFiltersStore((s) => s.filters);
	const setDurations = useGlobalFiltersStore((s) => s.setDurations);
	const setPriceRangePartFilter = useFilterStore((s) => s.setPriceRangePartFilter);
	const setAccoTypeFilter = useFilterStore((s) => s.setAccoTypeFilter);
	const setAwardsFilter = useFilterStore((s) => s.setAwardsFilter);
	const setTransportFilter = useFilterStore((s) => s.setTransportFilter);
	const setStarsFilter = useFilterStore((s) => s.setStarsFilter);
	const setDistancesFilter = useFilterStore((s) => s.setDistancesFilter);
	const setRatingFilter = useFilterStore((s) => s.setRatingFilter);
	const setFacilitiesFilter = useFilterStore((s) => s.setFacilitiesFilter);
	const setMealplansFilter = useFilterStore((s) => s.setMealplansFilter);
	const setThemesFilter = useFilterStore((s) => s.setThemesFilter);
	const setDepartureAirportFilter = useFilterStore((s) => s.setDepartureAirportFilter);
	const setGeoFilter = useFilterStore((s) => s.setGeoFilter);
	const setChainsFilter = useFilterStore((s) => s.setChainsFilter);
	const setTourOperatorsFilter = useFilterStore((s) => s.setTourOperatorsFilter);

	const { geoAggregations, aggregations } = useGetZooverCompositeSearch();

	const [minimumPrice, setMinimumPrice] = useState<number | undefined>(priceOrUndefined(stateFilters.priceMin));
	const [maximumPrice, setMaximumPrice] = useState<number | undefined>(priceOrUndefined(stateFilters.priceMax));
	const [priceFilterError, setPriceFilterError] = useState<PriceFilterValidationError>();

	useEffect(() => setMinimumPrice(priceOrUndefined(stateFilters.priceMin)), [stateFilters.priceMin]);
	useEffect(() => setMaximumPrice(priceOrUndefined(stateFilters.priceMax)), [stateFilters.priceMax]);

	const isOwnTransport = stateFilters.transport === 'OWN';
	const isMobileOrTablet = isMobile || isTablet;

	// callback for handling dataLayer events on search filters interactions
	const filterActionDataLayer = useCallback(
		(
			filterGroup: string,
			value: string,
			action: 'select' | 'deselect',
			molecule: string,
			atom: string,
			entity: Entity | null,
		) => {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/search').then(({ onFilterSelection }) => {
					track(onFilterSelection(filterGroup, value, action, molecule, atom, entity));
				}),
			);
		},
		[],
	);

	const selectDurationRangeNewLister = useCallback(
		(_e: SelectChangeEventType, selectedValue: SelectValue<string, boolean>) => {
			if (typeof selectedValue === 'string') {
				durationsRangeChange({ value: selectedValue, title: '' }, true);
			}
		},
		[durationsRangeChange],
	);
	const durationDayRangeLister = useParseDurationsFilterConfigLister(microCopies);

	const [selectedCountyIds, setSelectedCountyIds] = useState<string[]>([]);
	const [selectedRegionsSlugs, setSelectedRegionsSlugs] = useState<string[]>([]);

	const handleFilterOptionChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const element = event.target;
			const filterGroup = element.name.split('-')[0] as FilterOptionsKeys;

			if (filterGroup === 'durations') {
				const newValue = parseToDurationNumber(element.value);

				// If value is not a valid duration value, don't do anything.
				if (!newValue) return;

				// Default to setting the new value, unless the previous selected duration was an array,
				// then we either add it or remove it
				let newDurations = [newValue];

				if (Array.isArray(globalFilters.durations)) {
					if (globalFilters.durations.includes(newValue)) {
						newDurations = globalFilters.durations.filter((d) => d !== newValue);
					} else {
						newDurations = globalFilters.durations.concat(newValue);
					}
				}

				// In case the new value is an empty array, fall back to the previously set durationsRange
				if (!newDurations?.length && durationsRange) {
					durationsRangeChange(durationsRange, true);
				} else {
					setDurations(sortNumberArray(newDurations));
				}

				return;
			}

			const value = element.value;
			const filters = { ...stateFilters };

			if (Array.isArray(filters[filterGroup])) {
				if (filters[filterGroup]?.includes(value)) {
					// push a dataLayer event when deselecting a filter checkbox
					filterActionDataLayer(filterGroup, value, 'deselect', 'checkbox_group', 'checkbox', entitySearch);

					(filters[filterGroup] as string[]) = (filters[filterGroup] as string[]).filter(
						(item: string) => item !== value,
					);
				} else {
					// push a dataLayer event when selecting a filter checkbox
					filterActionDataLayer(filterGroup, value, 'select', 'checkbox_group', 'checkbox', entitySearch);

					(filters[filterGroup] as string[]) = [...filters[filterGroup], value];
				}
			} else {
				// push a dataLayer event when selecting a radio button filter
				filterActionDataLayer(filterGroup, value, 'select', 'radio_group', 'radio', entitySearch);
			}

			switch (filterGroup) {
				case 'accoType':
					setAccoTypeFilter(filters.accoType);
					break;
				case 'distances':
					setDistancesFilter(filters.distances);
					break;
				case 'facilities':
					setFacilitiesFilter(filters.facilities);
					break;
				case 'awards':
					setAwardsFilter(filters.awards);
					break;
				case 'stars':
					setStarsFilter(filters.stars);
					break;
				case 'transport':
					setTransportFilter(value as TransportType);
					break;
				case 'rating':
					setRatingFilter(value);
					break;

				case 'mealplans':
					setMealplansFilter(filters.mealplans);
					break;
				case 'departureAirport':
					setDepartureAirportFilter(filters.departureAirport);
					break;
				case 'themes':
					setThemesFilter(filters.themes);
					break;
				case 'countrySlugs':
				case 'regionSlugs':
				case 'citySlugs':
					const { countries, regions, cities } = getSelectedGeoFromSlugs(filters, geo);

					setGeoFilter({
						countries: countries.map((country) => ({ entityId: country.entityId, slug: country.slug })),
						regions: regions.map((region) => ({ entityId: region.entityId, slug: region.slug })),
						cities: cities.map((city) => ({ entityId: city.entityId, slug: city.slug })),
					});

					break;
				case 'chainsSlugs':
					const chains = getSelectedChainsBySlugs(filters.chainsSlugs);

					setChainsFilter(chains);
				case 'tourOperatorsSlugs':
					const tourOperators = getSelectedTourOperatorsBySlugs(filters.tourOperatorsSlugs);

					setTourOperatorsFilter(tourOperators);
			}
		},
		[
			stateFilters,
			globalFilters,
			filterActionDataLayer,
			entitySearch,
			setAccoTypeFilter,
			setDistancesFilter,
			setFacilitiesFilter,
			setAwardsFilter,
			setStarsFilter,
			setTransportFilter,
			setRatingFilter,
			durationsRange,
			setMealplansFilter,
			setDepartureAirportFilter,
			setThemesFilter,
			geo,
			setGeoFilter,
			setChainsFilter,
			setTourOperatorsFilter,
			durationsRangeChange,
			setDurations,
		],
	);

	const updateFilterState = useCallback(
		(type: 'priceMin' | 'priceMax'): void => {
			const largerThanZeroOrNull = (value: number | undefined): string | null => {
				if (value && value > 0) {
					return value.toString();
				}

				return null;
			};

			const value = type === 'priceMin' ? largerThanZeroOrNull(minimumPrice) : largerThanZeroOrNull(maximumPrice);

			setPriceRangePartFilter(type, value);
		},
		[minimumPrice, maximumPrice, setPriceRangePartFilter],
	);

	const revertedStarsFilter = useMemo(() => {
		if (!aggregations?.stars) {
			return [];
		}

		return aggregations.stars
			.filter(
				(star) =>
					STAR_OPTIONS.includes(star.key) && (star.doc_count !== 0 || stateFilters.stars.includes(star.key)),
			)
			.sort((a, b) => Number(b.key) - Number(a.key))
			.slice(0, 5);
	}, [aggregations?.stars, stateFilters.stars]);

	const revertedRatingFilter = useMemo(() => {
		return aggregations?.rating
			?.filter((rating) => rating.doc_count !== 0 || stateFilters.rating.includes(rating.key))
			.reverse();
	}, [aggregations?.rating, stateFilters.rating]);

	const filteredDistance = useMemo(() => {
		return aggregations?.distances?.filter(
			(distance) => distance.doc_count !== 0 || stateFilters.distances.includes(distance.key),
		);
	}, [aggregations?.distances, stateFilters.distances]);

	const filteredFacilities = useMemo(() => {
		return aggregations?.filters?.filter(
			(facility) => facility.doc_count !== 0 || stateFilters.facilities.includes(facility.key),
		);
	}, [aggregations?.filters, stateFilters.facilities]);

	const filteredAccoType = useMemo(() => {
		return aggregations?.accommodationType?.filter(
			(accoType) => accoType.doc_count !== 0 || stateFilters.accoType.includes(accoType.key),
		);
	}, [aggregations?.accommodationType, stateFilters.accoType]);

	const mealplanOptions = useMemo(() => {
		if (microCopies['config.filters.mealplanOptions']) {
			return microCopies['config.filters.mealplanOptions'].split(', ').map((code: string) => ({
				name: 'mealplans',
				label: microCopies[`filters.mealplans.labels.${code}`],
				value: code,
				checked: stateFilters.mealplans.includes(code),
			}));
		}
		return [];
	}, [microCopies, stateFilters.mealplans]);

	const awardsOptions = useMemo(
		() =>
			(['silver', 'gold'] as const).filter((award) => {
				const awardItems = aggregations && aggregations[`zoover_${award}_award`];
				return (
					(awardItems && awardItems[1] && awardItems[1].doc_count > 0) || stateFilters.awards.includes(award)
				);
			}),
		[aggregations, stateFilters.awards],
	);

	/** Only return chains where there's an accommodation found (doc_count > 0) */
	const chainsOptions = useMemo(() => {
		return routeParams.chains.filter((t) => {
			const aggregationChain = aggregations?.chain_entity_ids?.find((chain) => chain.key === String(t.entityId));

			return aggregationChain && aggregationChain.doc_count > 0;
		});
	}, [aggregations?.chain_entity_ids]);

	/** Show up to max 10 chains */
	const chainsShowAmount = useMemo(
		() => chainsOptions.filter((chain) => chain.isPopular).slice(0, 10)?.length,
		[chainsOptions],
	);

	const tourOperatorsOptions = useMemo(() => {
		if (!isOwnTransport) {
			return routeParams.tourOperators;
		}
	}, [isOwnTransport]);

	useEffect(() => {
		const selectedCountryIDs = getSelectedCountriesFromSlugs(stateFilters.countrySlugs, geo.countries).map(
			(country) => country.id,
		);
		selectedCountryIDs.length > 0 && setSelectedCountyIds(selectedCountryIDs as string[]);
	}, [geo.countries, stateFilters]);

	useEffect(() => {
		const allRegions = Object.values(geo.regions).flat();

		const selectedRegionsSlugs = allRegions
			.filter((r) => stateFilters.regionSlugs.includes(r.slug))
			.map((region) => region.slug);
		setSelectedRegionsSlugs(selectedRegionsSlugs);
	}, [geo.regions, stateFilters.regionSlugs]);

	const getRegionsTitle = useCallback(
		(countryId: string): string => {
			const countryName = geo.countries.find((c) => c.id === countryId)?.name;
			if (!countryName) return '';

			return inject(microCopies['search.popularregions'], {
				countryName,
			}).join('');
		},
		[geo.countries, microCopies],
	);

	const getCitiesTitle = useCallback(
		(regionSlug: string): string => {
			const allRegions = Object.values(geo.regions).flat();
			return inject(microCopies['search.popularcities'], {
				regionName: allRegions.find((r) => r.slug === regionSlug)?.name || '',
			}).join('');
		},
		[geo.regions, microCopies],
	);

	const filteredThemeOptions = useMemo((): Elastic.AggregationsItem[] | undefined => {
		const themes = microCopies['config.filters.themes'].split(', ');

		// Use themes from microcopy which is ordered, filter out any that we cannot map to the aggregations
		const mappedThemes = themes
			.map((t) => {
				const aggregationTheme = aggregations?.attributes?.find((theme) => theme.key === t);

				return {
					doc_count: aggregationTheme?.doc_count ?? 0,
					key: aggregationTheme?.key ?? '',
				};
			})
			.filter((t) => !!t.key);

		return mappedThemes.filter((theme) => theme.doc_count !== 0 || stateFilters.themes.includes(theme.key));
	}, [microCopies, aggregations?.attributes, stateFilters.themes]);

	const airportsOptions = useMemo(() => {
		const selectedAirports: string[] = stateFilters.departureAirport
			? (stateFilters.departureAirport as string[])
			: [];

		if (microCopies['config.departureAirports']) {
			return microCopies['config.departureAirports'].split(', ').map((code: string) => ({
				id: code,
				title: microCopies[`airportCountry.${code}`],
				value: selectedAirports.includes(code),
			}));
		}
		return [];
	}, [microCopies, stateFilters.departureAirport]);

	const trackPriceFilterSet = useCallback((label: 'priceMin' | 'priceMax', value: number | undefined) => {
		import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
			import('@vakantiesnl/services/src/gtm/search').then(({ onSetPriceFilter }) => {
				track(onSetPriceFilter(label, value ?? null));
			}),
		);
	}, []);

	const handlePriceContainerBlur = useCallback(
		(event: FocusEvent<HTMLDivElement>): void => {
			if (!event.currentTarget?.contains(event.relatedTarget)) {
				if (!priceFilterError) {
					if (stateFilters.priceMin != minimumPrice?.toString()) {
						updateFilterState('priceMin');
						trackPriceFilterSet('priceMin', minimumPrice);
					}

					if (stateFilters.priceMax != maximumPrice?.toString()) {
						updateFilterState('priceMax');
						trackPriceFilterSet('priceMax', maximumPrice);
					}
				}
			}
		},
		[
			priceFilterError,
			stateFilters.priceMin,
			stateFilters.priceMax,
			minimumPrice,
			maximumPrice,
			updateFilterState,
			trackPriceFilterSet,
		],
	);

	const handleMinPriceKeyDown = useCallback(
		(event: KeyboardEvent<HTMLInputElement>): void => {
			if (event.key === 'Enter' && !priceFilterError) {
				updateFilterState('priceMin');
				// user may have focused max price and input a value, then focused min price and after pressed Enter
				// handle this case:
				if (stateFilters.priceMax != maximumPrice?.toString()) {
					updateFilterState('priceMax');
					trackPriceFilterSet('priceMax', maximumPrice);
				}

				if (stateFilters.priceMin != minimumPrice?.toString()) {
					trackPriceFilterSet('priceMin', minimumPrice);
				}
			}
		},
		[
			priceFilterError,
			updateFilterState,
			stateFilters.priceMax,
			stateFilters.priceMin,
			maximumPrice,
			minimumPrice,
			trackPriceFilterSet,
		],
	);

	const handleMaxPriceKeyDown = useCallback(
		(event: KeyboardEvent<HTMLInputElement>): void => {
			if (event.key === 'Enter' && !priceFilterError) {
				updateFilterState('priceMax');
				// user may have focused min price and input a value, then focused max price and after pressed Enter
				// handle this case:
				if (stateFilters.priceMin != minimumPrice?.toString()) {
					updateFilterState('priceMin');
					trackPriceFilterSet('priceMin', minimumPrice);
				}

				if (stateFilters.priceMax != maximumPrice?.toString()) {
					trackPriceFilterSet('priceMax', maximumPrice);
				}
			}
		},
		[
			priceFilterError,
			updateFilterState,
			stateFilters.priceMin,
			stateFilters.priceMax,
			minimumPrice,
			maximumPrice,
			trackPriceFilterSet,
		],
	);

	const handlePriceRangeChange = useCallback(
		(value: number, setter: React.Dispatch<number | undefined>, validate: () => boolean): void => {
			if (isNaN(value)) {
				setter(undefined);
				setPriceFilterError(undefined);
			} else {
				setter(value);
				if (validate()) {
					setPriceFilterError(PriceFilterValidationError.MinimumExceedsMaximum);
				} else {
					setPriceFilterError(undefined);
				}
			}
		},
		[],
	);

	const parsePriceRangeInput = (value: string): number => Math.round(Math.abs(parseFloat(value)));

	const handleMinPriceChange = useCallback(
		(_id: string, value: string): void => {
			const parsed = parsePriceRangeInput(value);
			handlePriceRangeChange(parsed, setMinimumPrice, () => Boolean(maximumPrice && parsed > maximumPrice));
		},
		[maximumPrice, handlePriceRangeChange],
	);

	const handleMaxPriceChange = useCallback(
		(_id: string, value: string) => {
			const parsed = parsePriceRangeInput(value);
			handlePriceRangeChange(parsed, setMaximumPrice, () => Boolean(minimumPrice && parsed < minimumPrice));
		},
		[minimumPrice, handlePriceRangeChange],
	);

	const generateHiddenLink = useCallback(
		(checkbox: CheckBox, filters: FilterState, globalFilters: GlobalFiltersState): UrlObject | undefined =>
			isBot ? formatHiddenSearchFilterLink(checkbox, query, globalFilters, filters) : undefined,
		[isBot, query],
	);

	const renderRegionFilters = useCallback(() => {
		return selectedCountyIds
			.map((countryId) => geo.countries.find((c) => c.id === countryId))
			.map((country) => {
				const regions =
					country &&
					geo.regions[country.slug]?.filter((r) => {
						const isSelected = selectedRegionsSlugs.includes(r.slug);

						const count = geoAggregations.region_entity_ids[r.entityId];

						return isSelected || (!r.isDisabled && !!count);
					});

				if (!country || !regions?.length) {
					return null;
				}

				return (
					<li className={styles.filterListItem} key={country.id} data-cy={getRegionsTitle(country.id)}>
						<Accordion label={getRegionsTitle(country.id)} defaultExpanded={true}>
							<CheckboxGroup
								ariaLabel={getRegionsTitle(country.id)}
								checkboxes={regions.map((region) => {
									const checkbox: CheckBox = {
										name: 'regionSlugs',
										label: region.name,
										value: region.slug,
										checked: stateFilters.regionSlugs.includes(region.slug),
									};
									return {
										...checkbox,
										hiddenLink: generateHiddenLink(
											checkbox,
											{
												...stateFilters,
												countries: [{ entityId: country.entityId, slug: country.slug }],
												regions: [{ entityId: region.entityId, slug: region.slug }],
											},
											globalFilters,
										),
									};
								})}
								onChange={handleFilterOptionChange}
								orderByChecked={true}
								microCopies={microCopies}
								amountShown={5}
								disabled={isLoading}
							/>
						</Accordion>
					</li>
				);
			});
	}, [
		selectedCountyIds,
		geo.countries,
		geo.regions,
		styles.filterListItem,
		getRegionsTitle,
		handleFilterOptionChange,
		microCopies,
		isLoading,
		selectedRegionsSlugs,
		stateFilters,
		generateHiddenLink,
		globalFilters,
		geoAggregations.region_entity_ids,
	]);

	const renderCityFilters = useCallback(() => {
		return Object.keys(geo.cities)
			.filter((regionSlug) => selectedRegionsSlugs.includes(regionSlug))
			.map((regionSlug) => {
				const cities = geo.cities[regionSlug]?.filter((c) => {
					const isSelected = stateFilters.citySlugs.includes(c.slug);

					const count = geoAggregations.city_entity_ids[c.entityId];

					return isSelected || (!!count && !c.isDisabled);
				});

				if (!cities.length) {
					return null;
				}

				return (
					<li className={styles.filterListItem} key={regionSlug} data-cy={getCitiesTitle(regionSlug)}>
						<Accordion label={getCitiesTitle(regionSlug)} defaultExpanded={true}>
							<CheckboxGroup
								ariaLabel={getCitiesTitle(regionSlug)}
								checkboxes={cities.map((cityFilterOption) => {
									const checkbox: CheckBox = {
										name: 'citySlugs',
										label: cityFilterOption.name,
										value: cityFilterOption.slug,
										checked: stateFilters.citySlugs.includes(cityFilterOption.slug),
									};
									return {
										...checkbox,
										hiddenLink: generateHiddenLink(
											checkbox,
											{
												...stateFilters,
												regions: stateFilters.regions.filter((r) => r.slug === regionSlug),
											},
											globalFilters,
										),
									};
								})}
								onChange={handleFilterOptionChange}
								orderByChecked={true}
								microCopies={microCopies}
								amountShown={5}
								disabled={isLoading}
							/>
						</Accordion>
					</li>
				);
			});
	}, [
		geo.cities,
		selectedRegionsSlugs,
		styles.filterListItem,
		getCitiesTitle,
		handleFilterOptionChange,
		microCopies,
		isLoading,
		stateFilters,
		generateHiddenLink,
		globalFilters,
		geoAggregations.city_entity_ids,
	]);

	const filteredCountries = useMemo(() => {
		return geo.countries?.filter((c) => {
			const isSelected = stateFilters.countries.some((stateCountry) => stateCountry.entityId === c.entityId);

			const count = geoAggregations.country_entity_ids[c.entityId];

			return isSelected || (!c.isDisabled && count);
		});
	}, [geo.countries, stateFilters.countries, geoAggregations.country_entity_ids]);

	const [partyString, setPartyString] = useState(
		partyComposition
			? buildSummarizedPartyString(partyComposition, microCopies)
			: buildSummarizedPartyString(defaultParty, microCopies),
	);

	useEffect(() => {
		if (partyComposition) {
			setPartyString(buildSummarizedPartyString(partyComposition, microCopies));
		}
	}, [microCopies, partyComposition]);

	const onClosePartyPicker = useCallback(
		(party: Search.PartyComposition[]) => {
			!isEqual(party, partyComposition) && setPartyComposition(party);
		},
		[partyComposition, setPartyComposition],
	);

	const customRenderValue = useCallback(
		(_: SelectValue<string, boolean>, options: SelectOptionDefinition<string>[]) => {
			const durations = getSelectedDurations(globalFilters, stateFilters);

			if (isString(durations)) {
				const textFieldComponentValue = options.filter((option) => {
					return option.value.split(',').includes(durations.split('-')[0]);
				});
				return textFieldComponentValue[0].label;
			} else {
				const textFieldComponentValue = options.filter((option) =>
					option.value.split(',').includes(String(durations[0])),
				);
				return textFieldComponentValue[0].label;
			}
		},
		[globalFilters, stateFilters],
	);

	const allowTransport = useMemo(() => isZoover && getAllowTransportFilters(stateFilters), [stateFilters]);

	return (
		<div data-value={`filters-loading:${isLoading}`}>
			{isMobileOrTablet && (
				<FiltersLabels
					filters={stateFilters}
					onFilterRemove={onFilterRemove}
					onClear={onClear}
					microCopies={microCopies}
					geo={geo}
					isLoading={isLoading}
				/>
			)}
			{aggregations ? (
				<ul data-cy="filterListUl">
					{!isMobileOrTablet && (
						<li className={styles.filterListItem} data-cy="list-title">
							<Typography variant="headingLg" as="h3">
								{microCopies['common.filtersOpClean']}:
							</Typography>
						</li>
					)}
					{allowTransport && (
						<li className={styles.filterListItem} data-cy={microCopies['filters.transport']}>
							<Accordion label={microCopies['filters.transport']} defaultExpanded={true}>
								<RadioGroup
									ariaLabel={microCopies['filters.transport']}
									name={microCopies['filters.transport']}
									radios={['INCLUDING_FLIGHT', 'OWN'].map((transportOption) => ({
										group: 'transport',
										label: microCopies[`filters.transport.labels.${transportOption}`],
										value: transportOption,
										checked: stateFilters.transport === transportOption,
									}))}
									onChange={handleFilterOptionChange}
									disabled={isLoading}
								/>
							</Accordion>
						</li>
					)}

					<li className={styles.filterListItem} data-cy={microCopies['filters.pax']}>
						<Accordion label={microCopies['filters.pax']} defaultExpanded={true}>
							<SelectFormFieldMultiRoom
								label=""
								initialParty={partyComposition}
								defaultValue={partyString}
								onClose={onClosePartyPicker}
								hideMultiRoom={isOwnTransport}
							/>
						</Accordion>
					</li>

					<li className={styles.filterListItem} data-cy={microCopies['filters.departureDate']}>
						<Accordion label={microCopies['filters.departureDateHeading']} defaultExpanded={true}>
							<SelectFormFieldCalendar
								departureDate={departureDate}
								setDepartureDate={setDepartureDate}
								flexDays={flexibleDepartureDate}
								setFlexDays={setFlexibleDepartureDate}
								hasDrawerOnMobileTablet={true}
								hasFlexibilityOptions={true}
							/>
						</Accordion>
					</li>

					{filteredCountries.length > 0 && (
						<>
							<li className={styles.filterListItem} data-cy={microCopies['filters.destination']}>
								<Accordion label={microCopies['filters.destination']} defaultExpanded={true}>
									<CheckboxGroup
										ariaLabel={'filters.destination'}
										checkboxes={filteredCountries.map((country) => {
											const checkbox: CheckBox = {
												name: 'countrySlugs',
												label: country.name,
												value: country.slug,
												checked: stateFilters.countrySlugs.includes(country.slug),
											};

											return {
												...checkbox,
												hiddenLink: generateHiddenLink(
													checkbox,
													{
														...stateFilters,
														countries: [{ entityId: country.entityId, slug: country.slug }],
														regions: [],
													},
													globalFilters,
												),
											};
										})}
										onChange={handleFilterOptionChange}
										orderByChecked={true}
										microCopies={microCopies}
										amountShown={10}
										disabled={isLoading}
									/>
								</Accordion>
							</li>
							{stateFilters.countrySlugs.length > 0 && renderRegionFilters()}
							{stateFilters.regionSlugs.length > 0 &&
								stateFilters.countrySlugs.length === 1 &&
								renderCityFilters()}
						</>
					)}
					{!isOwnTransport && (
						<li className={styles.filterListItem} data-cy={microCopies['filters.departureAirport']}>
							<Accordion label={microCopies['filters.departureAirport']} defaultExpanded={true}>
								<CheckboxGroup
									ariaLabel={'filters.departureAirport'}
									checkboxes={airportsOptions.map((airportOption) => {
										const checkbox: CheckBox = {
											name: 'departureAirport',
											label: airportOption.title,
											value: airportOption.id,
											checked: airportOption.value,
										};

										return {
											...checkbox,
											hiddenLink: generateHiddenLink(checkbox, stateFilters, globalFilters),
										};
									})}
									onChange={handleFilterOptionChange}
									orderByChecked={true}
									microCopies={microCopies}
									amountShown={3}
									disabled={isLoading}
								/>
							</Accordion>
						</li>
					)}
					{selectedRange && (
						<li className={styles.filterListItem} data-cy={microCopies['common.duration']}>
							<Accordion label={microCopies['common.duration']} defaultExpanded={true}>
								<div className={styles.fullWidth}>
									{isDesktop ? (
										<SelectFormField
											label=""
											microCopies={microCopies}
											options={durationDayRangeLister}
											value={selectedRange.value}
											defaultValue={selectedRange.value}
											disabled={isLoading}
											drawerHeaderMicrocopy={microCopies['common.amount_of_days']}
											onChange={selectDurationRangeNewLister}
											inputClassName={styles.durationSelect}
											InputProps={durationStartAdornment}
										/>
									) : (
										<SelectFormField
											label=""
											microCopies={microCopies}
											options={durationDayRangeLister}
											value={selectedRange.value}
											defaultValue={selectedRange.value}
											disabled={isLoading}
											drawerHeaderMicrocopy={microCopies['common.amount_of_days']}
											onChange={selectDurationRangeNewLister}
											inputClassName={styles.durationSelect}
											variant="radio"
											customRenderValue={customRenderValue}
											listBoxClassName={styles.listBox}
										/>
									)}
									<CheckboxGroup
										ariaLabel={'common.duration'}
										checkboxes={selectedRange.value.split(',').map((durationDayOption) => ({
											name: 'durations',
											label: `${durationDayOption} ${microCopies['common.days']}`,
											value: durationDayOption,
											checked: Array.isArray(globalFilters.durations)
												? globalFilters.durations.includes(Number(durationDayOption))
												: false,
										}))}
										onChange={handleFilterOptionChange}
										microCopies={microCopies}
										disabled={isLoading}
									/>
								</div>
							</Accordion>
						</li>
					)}
					{!isOwnTransport && (
						<li className={styles.filterListItem} data-cy={microCopies['filters.pricePerPerson']}>
							<Accordion label={microCopies['filters.pricePerPerson']} defaultExpanded={true}>
								<PriceRangeFilter
									microCopies={microCopies}
									priceFilterDefaultMinimum={0}
									minimumPrice={minimumPrice}
									maximumPrice={maximumPrice}
									handleMinPriceChange={handleMinPriceChange}
									handleMaxPriceChange={handleMaxPriceChange}
									priceFilterError={priceFilterError}
									handleMinPriceKeyDown={handleMinPriceKeyDown}
									handleMaxPriceKeyDown={handleMaxPriceKeyDown}
									handlePriceContainerBlur={handlePriceContainerBlur}
									disabled={isLoading}
								/>
							</Accordion>
						</li>
					)}
					{!isOwnTransport && (
						<li className={styles.filterListItem} data-cy={microCopies['filters.mealplan']}>
							<Accordion label={microCopies['filters.mealplan']} defaultExpanded={true}>
								<CheckboxGroup
									ariaLabel={'filters.mealplan'}
									checkboxes={mealplanOptions.map((mealplanOption) => {
										const checkbox: CheckBox = {
											name: 'mealplans',
											label: mealplanOption.label,
											value: mealplanOption.value,
											checked: stateFilters.mealplans.includes(mealplanOption.value),
										};
										return {
											...checkbox,
											hiddenLink: generateHiddenLink(checkbox, stateFilters, globalFilters),
										};
									})}
									onChange={handleFilterOptionChange}
									orderByChecked={true}
									microCopies={microCopies}
									disabled={isLoading}
								/>
							</Accordion>
						</li>
					)}
					{!!filteredThemeOptions?.length && (
						<li className={styles.filterListItem} data-cy={microCopies['filters.theme']}>
							<Accordion label={microCopies['filters.theme']} defaultExpanded={true}>
								<CheckboxGroup
									ariaLabel={'filters.theme'}
									checkboxes={filteredThemeOptions?.map((themesOption) => {
										const checkbox: CheckBox = {
											name: 'themes',
											label: microCopies[`filters.themes.labels.${themesOption.key}`],
											value: themesOption.key,
											checked: stateFilters.themes.includes(themesOption.key),
										};
										return {
											...checkbox,
											hiddenLink: generateHiddenLink(checkbox, stateFilters, globalFilters),
										};
									})}
									onChange={handleFilterOptionChange}
									orderByChecked={true}
									microCopies={microCopies}
									amountShown={5}
									disabled={isLoading}
								/>
							</Accordion>
						</li>
					)}
					{showStars && !!revertedStarsFilter?.length && (
						<li className={styles.filterListItem} data-cy={microCopies['filters.stars']}>
							<Accordion label={microCopies['filters.stars']} defaultExpanded={true}>
								<CheckboxGroup
									ariaLabel={'filters.stars'}
									checkboxes={revertedStarsFilter.map((starsFilterOption) => {
										const checkbox: CheckBox = {
											name: 'stars',
											label: (
												<AccoStars starsCount={Number(starsFilterOption.key)} size="medium" />
											),
											value: starsFilterOption.key.toString(),
											checked: stateFilters.stars.includes(starsFilterOption.key),
										};
										return {
											...checkbox,
											hiddenLink: generateHiddenLink(checkbox, stateFilters, globalFilters),
										};
									})}
									onChange={handleFilterOptionChange}
									microCopies={microCopies}
									disabled={isLoading}
								/>
							</Accordion>
						</li>
					)}
					{!!revertedRatingFilter?.length && (
						<li className={styles.filterListItem} data-cy={microCopies['filters.rating']}>
							<Accordion label={microCopies['filters.rating']} defaultExpanded={true}>
								<RadioGroup
									ariaLabel={microCopies['filters.rating']}
									name={microCopies['filters.rating']}
									radios={revertedRatingFilter.map((ratingOption) => ({
										group: 'rating',
										label: microCopies[`filters.rating.labels.${ratingOption.key}`],
										value: ratingOption.key,
										checked: stateFilters.rating === ratingOption.key,
									}))}
									onChange={handleFilterOptionChange}
									disabled={isLoading}
								/>
							</Accordion>
						</li>
					)}
					{!!filteredAccoType?.length && (
						<li className={styles.filterListItem} data-cy={microCopies['filters.accoType']}>
							<Accordion label={microCopies['filters.accoType']} defaultExpanded={true}>
								<CheckboxGroup
									ariaLabel={'filters.accoType'}
									checkboxes={filteredAccoType.map((accoTypeFilterOption) => {
										const checkbox: CheckBox = {
											name: 'accoType',
											label: microCopies[`filters.accoType.labels.${accoTypeFilterOption.key}`],
											value: accoTypeFilterOption.key,
											counter: undefined,
											checked: stateFilters.accoType.includes(accoTypeFilterOption.key),
										};
										return {
											...checkbox,
											hiddenLink: generateHiddenLink(checkbox, stateFilters, globalFilters),
										};
									})}
									onChange={handleFilterOptionChange}
									orderByChecked={true}
									microCopies={microCopies}
									amountShown={5}
									disabled={isLoading}
								/>
							</Accordion>
						</li>
					)}
					{!!filteredFacilities?.length && (
						<li className={styles.filterListItem} data-cy={microCopies['filters.facilities']}>
							<Accordion label={microCopies['filters.facilities']} defaultExpanded={true}>
								<CheckboxGroup
									ariaLabel={'filters.facilities'}
									checkboxes={filteredFacilities.map((facilitiesFilterOption) => {
										const checkbox: CheckBox = {
											name: 'facilities',
											label:
												microCopies[
													`accommodationFilter.label.${facilitiesFilterOption.key}`
												] || facilitiesFilterOption.key,
											value: facilitiesFilterOption.key,
											checked: stateFilters.facilities.includes(facilitiesFilterOption.key),
										};
										return {
											...checkbox,
											hiddenLink: generateHiddenLink(checkbox, stateFilters, globalFilters),
										};
									})}
									onChange={handleFilterOptionChange}
									orderByChecked={true}
									microCopies={microCopies}
									amountShown={10}
									disabled={isLoading}
								/>
							</Accordion>
						</li>
					)}
					{!!filteredDistance?.length && (
						<li className={styles.filterListItem} data-cy={microCopies['filters.location']}>
							<Accordion label={microCopies['filters.location']} defaultExpanded={true}>
								<CheckboxGroup
									ariaLabel={'filters.location'}
									checkboxes={filteredDistance.map((distancesFilterOption) => {
										const checkbox: CheckBox = {
											name: 'distances',
											label: microCopies[
												`accommodationFilter.label.${distancesFilterOption.key}`
											],
											value: distancesFilterOption.key,
											checked: stateFilters.distances.includes(distancesFilterOption.key),
										};
										return {
											...checkbox,
											hiddenLink: generateHiddenLink(checkbox, stateFilters, globalFilters),
										};
									})}
									onChange={handleFilterOptionChange}
									orderByChecked={true}
									microCopies={microCopies}
									disabled={isLoading}
								/>
							</Accordion>
						</li>
					)}
					{!!awardsOptions.length && isZoover && (
						<li className={styles.filterListItem} data-cy={microCopies['filters.award']}>
							<Accordion label={microCopies['filters.award']} defaultExpanded={true}>
								<CheckboxGroup
									ariaLabel={'filters.award'}
									checkboxes={awardsOptions.map((award) => {
										const checkbox: CheckBox = {
											name: 'awards',
											label: microCopies[`accommodationFilter.label.${award}`] || award,
											value: award,
											checked: stateFilters.awards.includes(award),
										};

										return {
											...checkbox,
											hiddenLink: generateHiddenLink(checkbox, stateFilters, globalFilters),
										};
									})}
									onChange={handleFilterOptionChange}
									orderByChecked={true}
									microCopies={microCopies}
									disabled={isLoading}
								/>
							</Accordion>
						</li>
					)}
					{!!chainsOptions.length && chainsOptions.length > 0 && chainsShowAmount > 0 && (
						<li className={styles.filterListItem} data-cy={microCopies['filters.chains']}>
							<Accordion label={microCopies['filters.chains']} defaultExpanded={true}>
								<CheckboxGroup
									ariaLabel={'filters.chains'}
									checkboxes={chainsOptions.map((chain) => {
										const checkbox: CheckBox = {
											name: 'chainsSlugs',
											label: chain.name,
											value: chain.slug,
											checked: stateFilters.chainsSlugs.includes(chain.slug),
										};

										return {
											...checkbox,
											hiddenLink: generateHiddenLink(checkbox, stateFilters, globalFilters),
										};
									})}
									onChange={handleFilterOptionChange}
									orderByChecked={true}
									amountShown={chainsShowAmount}
									microCopies={microCopies}
									disabled={isLoading}
								/>
							</Accordion>
						</li>
					)}
					{!!tourOperatorsOptions?.length && tourOperatorsOptions?.length > 0 && (
						<li className={styles.filterListItem} data-cy={microCopies['filters.tourOperator']}>
							<Accordion label={microCopies['filters.tourOperator']} defaultExpanded={true}>
								<CheckboxGroup
									ariaLabel={'filters.tourOperator'}
									checkboxes={tourOperatorsOptions.map((tourOperator) => {
										const checkbox: CheckBox = {
											name: 'tourOperatorsSlugs',
											label: tourOperator.name,
											value: tourOperator.slug,
											checked: stateFilters.tourOperatorsSlugs.includes(tourOperator.slug),
										};

										return {
											...checkbox,
											hiddenLink: generateHiddenLink(checkbox, stateFilters, globalFilters),
										};
									})}
									onChange={handleFilterOptionChange}
									orderByChecked={true}
									microCopies={microCopies}
									disabled={isLoading}
								/>
							</Accordion>
						</li>
					)}
				</ul>
			) : (
				<FilterListSkeleton />
			)}
		</div>
	);
};
